import React, { useState, useRef } from 'react';
import { Btn, Modal, SearchableSelect } from './UI';
import { fmtNum } from '../utils';
import * as api from '../api';

export default function BulkQSInstallationModal({
  onClose, onSuccess, projectId, toast, boqItems, subsections, disciplines, users, costCategories, materials
}) {
  const [saving, setSaving] = useState(false);
  const tableContainerRef = useRef(null);

  const generateCode = () => {
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `INST-${dateStr}-${randomStr}`;
  };

  const [commonFields, setCommonFields] = useState({
    installation_date: new Date().toISOString().slice(0, 10),
    dmr_sub_section_id: '',
    discipline_id: '',
    cost_category_id: '',
    zone: '',
    location: ''
  });

  const createEmptyLine = () => ({
    id: Math.random().toString(36).substr(2, 9),
    installation_code: generateCode(),
    unit_of_measure: 'Nr',
    boq_item_id: '',
    boq_component_id: '',
    material_id: '',
    brand_name: '',
    description: '',
    boq_quantity: '',
    purchased_qty: '',
    issued_qty: '',
    previous_installed_qty: '',
    current_installed_qty: '',
    total_installed_qty: 0,
    remaining_qty: 0,
    offcut_qty: '',
    reusable_offcut_qty: '',
    damaged_qty: '',
    returned_qty: '',
    wastage_qty: '',
    lost_qty: '',
    uninstalled_issued_qty: '',
    variance_qty: '',
    installation_percentage: '',
    supervisor_id: '',
    qs_employee_id: '',
    installer_name: '',
    qa_qc_status: '',
    inspection_status: '',
    remarks: ''
  });

  const [lines, setLines] = useState([createEmptyLine()]);

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(line => {
      if (line.id !== id) return line;

      const updated = { ...line, [field]: value };

      // Auto calculations
      if (['previous_installed_qty', 'current_installed_qty', 'issued_qty'].includes(field)) {
        const prevQty = parseFloat(updated.previous_installed_qty) || 0;
        const currQty = parseFloat(updated.current_installed_qty) || 0;
        const issuedQty = parseFloat(updated.issued_qty) || 0;
        const boqQty = parseFloat(updated.boq_quantity) || 0;

        updated.total_installed_qty = prevQty + currQty;
        updated.remaining_qty = issuedQty - updated.total_installed_qty;

        if (boqQty > 0) {
          updated.installation_percentage = ((updated.total_installed_qty / boqQty) * 100).toFixed(2);
        }
      }

      return updated;
    }));
  };

  const addLine = () => {
    setLines(prev => [...prev, createEmptyLine()]);
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
      }
    }, 50);
  };

  const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));

  const updateCommonField = (field, value) => {
    setCommonFields(prev => ({ ...prev, [field]: value }));
  };

  const saveTransactions = async () => {
    if (!commonFields.dmr_sub_section_id || !commonFields.discipline_id || !commonFields.cost_category_id || !commonFields.installation_date) {
      toast?.show?.('Please fill all required common fields at the top', 'error');
      return;
    }

    // Validation
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.material_id || !l.current_installed_qty) {
        toast?.show?.(`Line ${i + 1} is missing required fields (Material or Current Qty)`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        ...commonFields,
        lines: lines.map(line => {
          const l = { ...line };
          delete l.id;
          Object.keys(l).forEach(key => {
            if (l[key] === '') l[key] = null;
          });
          return l;
        })
      };

      await api.createBulkDailyInstalled(payload);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast?.show?.(e.message || 'Failed to submit bulk entries', 'error');
    }
    setSaving(false);
  };

  const inputStyle = { background: '#f8fafc', color: '#1e293b', border: '1px solid #cbd5e1', padding: '6px 10px', fontSize: 13, borderRadius: 6 };
  const thStyle = { padding: '12px 8px', fontWeight: 700, fontSize: 12, color: '#ffffff', textTransform: 'uppercase', letterSpacing: 0.5, borderRight: '1px solid rgba(255,255,255,0.2)', whiteSpace: 'nowrap', textAlign: 'center', background: '#0b318bff', position: 'sticky', top: 0, zIndex: 10 };
  const tdStyle = { padding: '8px', borderRight: '1px solid #e2e8f0', verticalAlign: 'middle' };
  // Matches the .qs-modal-table select CSS below, for SearchableSelect instances
  // (that rule only targets native <select> elements, not our div-based trigger).
  const qsSelectStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 500, background: '#fff', color: '#0f172a' };
  const boqItemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);

  const modalContainerStyle = { padding: 0, overflow: 'hidden', background: '#ffffff', borderRadius: 12, display: 'flex', flexDirection: 'column', maxHeight: '90vh' };
  const headerStyle = { background: '#0b318bff', padding: '16px 24px', color: '#ffffff', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
  const footerStyle = { background: '#f8fafc', padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid black', flexShrink: 0 };

  return (
    <Modal onClose={onClose} width={1500} hideHeader>
      <div style={modalContainerStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#ffffff', letterSpacing: '0.02em' }}>
            Add Bulk Installation Records
          </h2>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
            fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', background: '#f1f5f9', borderBottom: '1px solid black', display: 'flex', flexWrap: 'wrap', gap: '16px 24px', flexShrink: 0 }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>INSTALLATION DATE *</label>
            <input type="date" value={commonFields.installation_date} onChange={e => updateCommonField('installation_date', e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: '1 1 250px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>SUB-SECTION *</label>
            <select value={commonFields.dmr_sub_section_id} onChange={e => updateCommonField('dmr_sub_section_id', e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}>
              <option value="">Select...</option>
              {subsections.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>DISCIPLINE *</label>
            <select value={commonFields.discipline_id} onChange={e => updateCommonField('discipline_id', e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}>
              <option value="">Select...</option>
              {disciplines.map(b => <option key={b.discipline_id || b.id} value={b.discipline_id || b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>COST COMPONENT *</label>
            <select value={commonFields.cost_category_id} onChange={e => updateCommonField('cost_category_id', e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}>
              <option value="">Select...</option>
              {(costCategories || []).map(c => <option key={c.cost_category_id || c.id} value={c.cost_category_id || c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>ZONE</label>
            <input type="text" value={commonFields.zone} onChange={e => updateCommonField('zone', e.target.value)} placeholder="e.g. Zone A" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>LOCATION / FLOOR</label>
            <input type="text" value={commonFields.location} onChange={e => updateCommonField('location', e.target.value)} placeholder="e.g. 2nd Floor" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <style>{`
            .qs-modal-table-container {
              overflow-x: auto;
              overflow-y: auto;
              flex: 1;
              background: #f8fafc;
            }
            .qs-modal-table {
              width: 100%;
              border-collapse: collapse;
              min-width: 4500px;
            }
            .qs-modal-table input, .qs-modal-table select {
              width: 100%;
              padding: 8px 12px;
              border: 1px solid #cbd5e1;
              border-radius: 6px;
              outline: none;
              box-sizing: border-box;
              color: #0f172a;
              font-weight: 500;
              font-size: 13px;
              font-family: inherit;
              background: #ffffff;
            }
            .qs-modal-table input:focus, .qs-modal-table select:focus {
              border-color: #3b82f6;
              box-shadow: 0 0 0 1px #3b82f6;
            }
            .qs-modal-table input[disabled], .qs-modal-table select[disabled], .qs-modal-table input[readOnly] {
              background: #f1f5f9;
              color: #64748b;
              cursor: not-allowed;
            }
          `}</style>

          <div className="qs-modal-table-container" ref={tableContainerRef}>
            <table className="qs-modal-table">
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 50, minWidth: 50, maxWidth: 50, textAlign: 'center', position: 'sticky', left: 0, zIndex: 11 }}>S/N</th>
                  <th style={{ ...thStyle, width: 60, minWidth: 60, maxWidth: 60, textAlign: 'center', position: 'sticky', left: 50, zIndex: 11 }}>Action</th>
                  <th style={{ ...thStyle, width: 200, minWidth: 200, maxWidth: 200, position: 'sticky', left: 110, zIndex: 11 }}>Inst Code</th>
                  <th style={{ ...thStyle, width: 180, minWidth: 180, maxWidth: 180, position: 'sticky', left: 310, zIndex: 11 }}>BOQ Code</th>
                  <th style={{ ...thStyle, width: 180, minWidth: 180, maxWidth: 180, position: 'sticky', left: 490, zIndex: 11 }}>Sub BOQ</th>
                  <th style={{ ...thStyle, width: 200, minWidth: 200, maxWidth: 200, position: 'sticky', left: 670, zIndex: 11 }}>Material *</th>
                  <th style={{ ...thStyle, minWidth: 300 }}>BOQ Description</th>
                  <th style={{ ...thStyle, minWidth: 120 }}>Brand</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Unit</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>BOQ Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Purchased Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Issued Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Prev Inst</th>
                  <th style={{ ...thStyle, minWidth: 100, borderBottom: '3px solid #3b82f6' }}>Inst Qty *</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Offcut Qty</th>
                  <th style={{ ...thStyle, minWidth: 120 }}>Reusable Offcut</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Damaged Qty</th>
                  <th style={{ ...thStyle, minWidth: 120 }}>Uninstalled Issued</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Variance Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Returned Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Wastage Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Lost Qty</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Total Inst</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Remaining</th>
                  <th style={{ ...thStyle, minWidth: 100 }}>Inst %</th>
                  <th style={{ ...thStyle, minWidth: 150 }}>Installer</th>
                  <th style={{ ...thStyle, minWidth: 150 }}>Supervisor</th>
                  <th style={{ ...thStyle, minWidth: 150 }}>QS Name</th>
                  <th style={{ ...thStyle, minWidth: 120 }}>QA/QC</th>
                  <th style={{ ...thStyle, minWidth: 120 }}>Inspection</th>
                  <th style={{ ...thStyle, minWidth: 250 }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const selectedBoq = boqItemsArray.find(i => String(i.boq_item_id || i.id) === String(line.boq_item_id));
                  const boqComponents = selectedBoq?.components || [];

                  return (
                    <tr key={line.id} style={{ borderBottom: '1px solid #e2e8f0', background: '#ffffff' }}>
                      <td style={{ ...tdStyle, width: 50, minWidth: 50, maxWidth: 50, textAlign: 'center', fontWeight: 600, color: '#475569', position: 'sticky', left: 0, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>{index + 1}</td>
                      <td style={{ ...tdStyle, width: 60, minWidth: 60, maxWidth: 60, textAlign: 'center', position: 'sticky', left: 50, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>
                        {lines.length > 1 && (
                          <button onClick={() => removeLine(line.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" /></svg>
                          </button>
                        )}
                      </td>
                      <td style={{ ...tdStyle, width: 200, minWidth: 200, maxWidth: 200, position: 'sticky', left: 110, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>
                        <input type="text" value={line.installation_code || ''} readOnly placeholder="Auto-generated" style={{ width: '100%', background: '#e2e8f0', border: '1.5px dashed #0f172a', borderRadius: 6, padding: '6px 8px', fontWeight: 800, color: '#16a34a', textAlign: 'center', fontSize: 13, letterSpacing: '0.02em' }} />
                      </td>
                      <td style={{ ...tdStyle, width: 180, minWidth: 180, maxWidth: 180, position: 'sticky', left: 310, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.boq_item_id}
                          onChange={v => {
                            const boq = boqItemsArray.find(i => String(i.boq_item_id || i.id) === String(v));
                            updateLine(line.id, 'boq_item_id', v);
                            updateLine(line.id, 'boq_component_id', '');
                            if (boq) {
                              updateLine(line.id, 'description', boq.description || '');
                              updateLine(line.id, 'unit_of_measure', boq.unit_of_measure || 'Nr');
                              updateLine(line.id, 'boq_quantity', boq.budgeted_quantity || boq.quantity || '');
                            }
                          }}
                          options={boqItemsArray.map(i => ({ value: i.boq_item_id || i.id, label: `${i.boq_code || ''} — ${i.description}`.trim() }))}
                          placeholder="Select BOQ Code..."
                        />
                      </td>
                      <td style={{ ...tdStyle, width: 180, minWidth: 180, maxWidth: 180, position: 'sticky', left: 490, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.boq_component_id}
                          onChange={v => updateLine(line.id, 'boq_component_id', v)}
                          disabled={boqComponents.length === 0}
                          options={boqComponents.map(c => ({ value: c.component_id || c.id, label: c.description || c.name }))}
                          placeholder={boqComponents.length === 0 ? 'N/A' : 'Select...'}
                        />
                      </td>
                      <td style={{ ...tdStyle, width: 200, minWidth: 200, maxWidth: 200, position: 'sticky', left: 670, background: '#ffffff', zIndex: 9, borderRight: '1px solid #cbd5e1' }}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.material_id}
                          onChange={v => updateLine(line.id, 'material_id', v)}
                          options={(materials || []).map(m => ({ value: m.material_id || m.id, label: m.name || m.description }))}
                          placeholder="Select Material..."
                        />
                      </td>
                      <td style={tdStyle}><input type="text" value={line.description || ''} readOnly placeholder="Auto-filled" /></td>
                      <td style={tdStyle}><input type="text" value={line.brand_name || ''} onChange={e => updateLine(line.id, 'brand_name', e.target.value)} /></td>
                      <td style={tdStyle}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.unit_of_measure}
                          onChange={v => updateLine(line.id, 'unit_of_measure', v)}
                          options={['M', 'M2', 'M3', 'Nr', 'KG', 'PCS', 'LS', 'Lot'].map(s => ({ value: s, label: s }))}
                        />
                      </td>
                      <td style={tdStyle}><input type="number" value={line.boq_quantity || ''} onChange={e => updateLine(line.id, 'boq_quantity', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.purchased_qty || ''} onChange={e => updateLine(line.id, 'purchased_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.issued_qty || ''} onChange={e => updateLine(line.id, 'issued_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.previous_installed_qty || ''} onChange={e => updateLine(line.id, 'previous_installed_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.current_installed_qty || ''} onChange={e => updateLine(line.id, 'current_installed_qty', e.target.value)} style={{ borderColor: '#3b82f6', borderWidth: 2 }} /></td>
                      <td style={tdStyle}><input type="number" value={line.offcut_qty || ''} onChange={e => updateLine(line.id, 'offcut_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.reusable_offcut_qty || ''} onChange={e => updateLine(line.id, 'reusable_offcut_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.damaged_qty || ''} onChange={e => updateLine(line.id, 'damaged_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.uninstalled_issued_qty || ''} onChange={e => updateLine(line.id, 'uninstalled_issued_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.variance_qty || ''} onChange={e => updateLine(line.id, 'variance_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.returned_qty || ''} onChange={e => updateLine(line.id, 'returned_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.wastage_qty || ''} onChange={e => updateLine(line.id, 'wastage_qty', e.target.value)} /></td>
                      <td style={tdStyle}><input type="number" value={line.lost_qty || ''} onChange={e => updateLine(line.id, 'lost_qty', e.target.value)} /></td>

                      <td style={tdStyle}>
                        <div style={{ background: '#ecfdf5', padding: '6px 8px', borderRadius: 6, color: '#047857', fontWeight: 700, border: '1px solid #10b981', textAlign: 'center' }}>
                          {fmtNum(line.total_installed_qty)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ background: '#fefce8', padding: '6px 8px', borderRadius: 6, color: '#a16207', fontWeight: 700, border: '1px solid #eab308', textAlign: 'center' }}>
                          {fmtNum(line.remaining_qty)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: 6, color: '#334155', fontWeight: 700, border: '1px solid #cbd5e1', textAlign: 'center' }}>
                          {line.installation_percentage || '0.0'}%
                        </div>
                      </td>

                      <td style={tdStyle}><input type="text" value={line.installer_name || ''} onChange={e => updateLine(line.id, 'installer_name', e.target.value)} /></td>
                      <td style={tdStyle}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.supervisor_id}
                          onChange={v => updateLine(line.id, 'supervisor_id', v)}
                          options={(users || []).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))}
                        />
                      </td>
                      <td style={tdStyle}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.qs_employee_id}
                          onChange={v => updateLine(line.id, 'qs_employee_id', v)}
                          options={(users || []).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))}
                        />
                      </td>
                      <td style={tdStyle}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.qa_qc_status}
                          onChange={v => updateLine(line.id, 'qa_qc_status', v)}
                          options={[
                            { value: 'Pending', label: 'Pending' },
                            { value: 'Passed', label: 'Passed' },
                            { value: 'Failed', label: 'Failed' },
                          ]}
                        />
                      </td>
                      <td style={tdStyle}>
                        <SearchableSelect
                          style={qsSelectStyle}
                          value={line.inspection_status}
                          onChange={v => updateLine(line.id, 'inspection_status', v)}
                          options={[
                            { value: 'Pending', label: 'Pending' },
                            { value: 'Passed', label: 'Passed' },
                            { value: 'Failed', label: 'Failed' },
                          ]}
                        />
                      </td>
                      <td style={tdStyle}><input type="text" value={line.remarks || ''} onChange={e => updateLine(line.id, 'remarks', e.target.value)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', background: '#ffffff', borderTop: '1px solid #cbd5e1', flexShrink: 0 }}>
            <button onClick={addLine} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0b318bff', border: 'none', color: '#ffffff', padding: '10px 24px', borderRadius: 999, fontSize: 14, fontWeight: 900, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(11, 49, 139, 0.2)' }}>
              Add Another Line
            </button>
          </div>
        </div>

        <div style={footerStyle}>
          <Btn onClick={onClose} style={{ background: '#ef4444', color: 'white', padding: '10px 32px', fontSize: 14, borderRadius: 8, border: 'none' }}>
            Cancel
          </Btn>
          <Btn onClick={saveTransactions} disabled={saving} style={{ background: '#16a34a', color: 'white', padding: '10px 32px', fontSize: 14, borderRadius: 8, border: 'none' }}>
            {saving ? 'Submitting...' : 'Submit'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
